import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Checkbox, Grid, IconButton, Divider,
  InputAdornment, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip, Fade, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Search as SearchIcon, Save as SaveIcon,
  Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon,
  HealthAndSafety as InsuranceIcon, Scanner as ScannerIcon,
  CheckCircle as CheckCircleIcon, Delete as DeleteIcon, ContentCopy as CopyIcon,
  MedicalServices as MedicalIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import {
  loadInsurance, loadScanCategoriesbd, getScanInsurances,
  updateInsuredScan, deleteInsuredScan, saveNewScan,
  bulkApplyScanInsurance, moveScanToInsurancedb
} from '../BaseURL';

import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

/* ── Shared styles ──────────────────────────────────────── */
const cellInputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.78rem',
    '& input': { py: '4px', px: '8px' },
    '& fieldset': { borderColor: '#e0e0e0' },
    '&:hover fieldset': { borderColor: '#11998e' },
    '&.Mui-focused fieldset': { borderColor: '#11998e' },
  },
};

/* ── Editable Row ───────────────────────────────────────── */
const EditableRow = ({ row, idx, selected, onSelectToggle, onChange, onUpdate, onDelete, updating, deleting }) => {
  const tdSx = { borderBottom: '1px solid #f0f0f0', py: 0.75, px: 1 };
  const rowId = String(row.id);
  return (
    <TableRow sx={{
      '&:nth-of-type(odd)': { background: selected ? 'rgba(17,153,142,0.10)' : '#f9faff' },
      '&:nth-of-type(even)': { background: selected ? 'rgba(17,153,142,0.10)' : '#fff' },
      '&:hover': { background: selected ? 'rgba(17,153,142,0.15)' : 'rgba(17,153,142,0.04)' },
      transition: 'background 0.2s',
    }}>
      <TableCell sx={{ ...tdSx, width: 48, textAlign: 'center', px: 0.5 }}>
        <Checkbox size="small" checked={selected} onChange={() => onSelectToggle(rowId)}
          sx={{ color: '#11998e', '&.Mui-checked': { color: '#11998e' }, p: 0.5 }} />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 36, textAlign: 'center', color: '#9e9e9e', fontSize: '0.72rem', fontWeight: 500 }}>{idx + 1}</TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 150 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1a237e' }}>{row.status || '—'}</Typography>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" type="number" fullWidth value={row.tariff ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('tariff', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" type="number" fullWidth value={row.topup ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('topup', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 110 }}>
        <TextField size="small" fullWidth value={row.GDRG ?? ''} onChange={e => onChange('GDRG', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 180, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
          <Button size="small" variant="contained" onClick={() => onUpdate(row)} disabled={updating || deleting}
            startIcon={updating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            sx={{ fontSize: '0.7rem', px: 1.5, py: 0.5, background: '#11998e', color: 'white', fontWeight: 600, textTransform: 'none', '&:hover': { background: '#0e8a7f' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
            {updating ? 'Saving...' : 'Update'}
          </Button>
          <Button size="small" variant="contained" onClick={() => onDelete(row)} disabled={deleting || updating}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{ fontSize: '0.7rem', px: 1.5, py: 0.5, background: '#e53935', color: 'white', fontWeight: 600, textTransform: 'none', '&:hover': { background: '#c62828' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
            {deleting ? '...' : 'Delete'}
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};


/* ── Bulk Apply Dialog ──────────────────────────────────── */
const BulkApplyDialog = ({ open, onClose, sourceRow, insuranceProviders, loadingInsurance, onSaved, existingStatuses, selectedRows }) => {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [editTariff, setEditTariff] = useState('');
  const [editTopup, setEditTopup] = useState('');
  const [editDRG, setEditDRG] = useState('');

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  useEffect(() => {
    if (open && sourceRow) {
      const preChecked = {};
      if (selectedRows?.length > 0) {
        selectedRows.forEach(row => {
          const match = insuranceProviders.find(p => p.name === row.status);
          if (match) preChecked[match.id] = true;
        });
      }
      setSelectedInsurances(preChecked);
      setInsuranceSearch('');
    }
  }, [open, sourceRow, selectedRows, insuranceProviders]);

  const handleInsuranceToggle = (id) => setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { all[i.id] = true; });
    setSelectedInsurances(all);
  };
  const handleClose = () => { setSelectedInsurances({}); setInsuranceSearch(''); onClose(); };
  const getProviderName = (id) => insuranceProviders.find(p => String(p.id) === String(id))?.name || '';

  const handleUpdate = async () => {
    if (selectedCount === 0) { await Swal.fire({ title: 'No Selection', text: 'Select at least one insurance.', icon: 'warning', confirmButtonColor: '#11998e' }); return; }
    if (editTariff === '' || editTopup === '') { await Swal.fire({ title: 'Validation', text: 'Fill in tariff and top-up.', icon: 'warning', confirmButtonColor: '#11998e' }); return; }
    const confirm = await Swal.fire({
      title: 'Apply Pricing?', html: `Apply new values to <b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''}?`,
      icon: 'question', showCancelButton: true, confirmButtonColor: '#11998e', cancelButtonColor: '#9e9e9e', confirmButtonText: 'Yes, Apply'
    });
    if (!confirm.isConfirmed) return;
    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => ({
        scan: sourceRow.scan,
        tariff: parseFloat(editTariff),
        topup: parseFloat(editTopup),
        GDRG: editDRG.trim(),
        status: getProviderName(id),
      }));
      const response = await axios.post(bulkApplyScanInsurance, { rows });
      if (response.data.success) {
        await Swal.fire({ title: 'Applied!', html: `<b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''} updated.`, icon: 'success', timer: 2500, showConfirmButton: false, timerProgressBar: true });
        if (onSaved) onSaved(); handleClose();
      } else throw new Error(response.data.message || 'Apply failed');
    } catch (err) { await Swal.fire({ title: 'Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (selectedCount === 0) { await Swal.fire({ title: 'No Selection', text: 'Select at least one insurance to delete.', icon: 'warning', confirmButtonColor: '#11998e' }); return; }
    const confirm = await Swal.fire({
      title: 'Delete Records?', html: `Delete pricing for <b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''}?`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53935', cancelButtonColor: '#9e9e9e', confirmButtonText: 'Yes, Delete'
    });
    if (!confirm.isConfirmed) return;
    setDeleting(true);
    try {
      // For delete, we need the actual row id for each selected insurance for this scan.
      // selectedRows already contains the rows the user checked on the table; use those ids.
      const promises = (selectedRows || [])
        .filter(r => selectedInsuranceIds.some(id => getProviderName(id) === r.status))
        .map(r => axios.post(deleteInsuredScan, { id: r.id }));
      await Promise.all(promises);
      await Swal.fire({ title: 'Deleted!', html: `<b>${selectedCount}</b> record${selectedCount !== 1 ? 's' : ''} deleted.`, icon: 'success', timer: 2000, showConfirmButton: false, timerProgressBar: true });
      if (onSaved) onSaved(); handleClose();
    } catch (err) { await Swal.fire({ title: 'Delete Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setDeleting(false); }
  };

  const filteredProviders = insuranceProviders.filter(i => i.name?.toLowerCase().includes(insuranceSearch.toLowerCase()));

  if (!sourceRow) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ background: '#11998e', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CopyIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>Apply to Insurance Providers</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>{sourceRow.scan}</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Grid container>

          {/* LEFT: Edit fields + action buttons */}
          <Grid item xs={12} md={5} sx={{ p: 3, borderRight: { md: '1px solid #e8eaf6' }, background: '#fafbff', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3949ab', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>Edit Values</Typography>
              <Typography variant="caption" sx={{ color: '#757575' }}>Will apply to all selected insurances on the right</Typography>
            </Box>
            <TextField fullWidth size="small" label="Tariff GH¢" type="number"
              value={editTariff} onChange={e => setEditTariff(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
            <TextField fullWidth size="small" label="Top-up GH¢" type="number"
              value={editTopup} onChange={e => setEditTopup(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
            <TextField fullWidth size="small" label="DRG Code (optional)"
              value={editDRG} onChange={e => setEditDRG(e.target.value)}
              placeholder="e.g., SCAN001" sx={cellInputSx} />
            <Divider />
            <Button fullWidth variant="contained" onClick={handleUpdate}
              disabled={saving || deleting || selectedCount === 0}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{ background: '#11998e', color: 'white', fontWeight: 700, textTransform: 'none', py: 1, '&:hover': { background: '#0e8a7f' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
              {saving ? 'Updating...' : `Update ${selectedCount > 0 ? `${selectedCount} ` : ''}Insurance${selectedCount !== 1 ? 's' : ''}`}
            </Button>
            <Button fullWidth variant="contained" onClick={handleDelete}
              disabled={deleting || saving || selectedCount === 0}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
              sx={{ background: '#e53935', color: 'white', fontWeight: 700, textTransform: 'none', py: 1, '&:hover': { background: '#c62828' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
              {deleting ? 'Deleting...' : `Delete ${selectedCount > 0 ? `${selectedCount} ` : ''}Insurance${selectedCount !== 1 ? 's' : ''}`}
            </Button>
            {selectedCount === 0 && <Typography variant="caption" sx={{ color: '#e53935', textAlign: 'center', fontSize: '0.7rem' }}>Select at least one insurance on the right</Typography>}
          </Grid>

          {/* RIGHT: All insurance providers */}
          <Grid item xs={12} md={7} sx={{ p: 3, display: 'flex', flexDirection: 'column', background: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3949ab', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                Select Insurances
              </Typography>
              {selectedCount > 0 && <Chip size="small" label={`${selectedCount} selected`} sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }} />}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll} sx={{ borderColor: '#11998e', color: '#11998e', fontSize: '0.72rem', py: 0.4 }}>Select All</Button>
              <Button size="small" variant="outlined" onClick={() => setSelectedInsurances({})} sx={{ borderColor: '#11998e', color: '#11998e', fontSize: '0.72rem', py: 0.4 }}>Deselect All</Button>
            </Box>
            <TextField fullWidth size="small" placeholder="Search insurance..." value={insuranceSearch}
              onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5 }}
              InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} /></InputAdornment> }} />
            <Box sx={{ flex: 1, overflowY: 'auto', border: '1px solid #e8eaf6', borderRadius: 2, minHeight: 300,
              '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' } }}>
              {loadingInsurance
                ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
                : filteredProviders.length === 0
                  ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                      <Typography variant="body2" color="text.secondary">No providers found</Typography>
                    </Box>
                  : <Box sx={{ p: 1 }}>
                      {filteredProviders.map(ins => (
                        <Box key={ins.id} onClick={() => handleInsuranceToggle(ins.id)}
                          sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1, cursor: 'pointer', mb: 0.5,
                            background: selectedInsurances[ins.id] ? 'rgba(17,153,142,0.08)' : 'transparent',
                            border: selectedInsurances[ins.id] ? '1px solid rgba(17,153,142,0.25)' : '1px solid transparent',
                            '&:hover': { background: 'rgba(17,153,142,0.06)' } }}>
                          <Checkbox size="small" checked={!!selectedInsurances[ins.id]}
                            onClick={e => e.stopPropagation()} onChange={() => handleInsuranceToggle(ins.id)}
                            sx={{ p: 0.5, color: '#11998e', '&.Mui-checked': { color: '#11998e' } }} />
                          <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem' }}>{ins.name}</Typography>
                          {existingStatuses.includes(ins.name) && (
                            <Chip label="Exists" size="small" sx={{ ml: 'auto', fontSize: '0.65rem', height: 18, background: '#e8f5e9', color: '#2e7d32' }} />
                          )}
                        </Box>
                      ))}
                    </Box>
              }
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
        <Button onClick={handleClose} variant="outlined" disabled={saving || deleting} sx={{ color: '#757575', borderColor: '#e0e0e0' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── New Scan Pricing Dialog ─────────────────────────────── */
const NewScanPricingDialog = ({ open, onClose, selectedScan, insuranceProviders, loadingInsurance, onSaved, existingStatuses }) => {
  const [saving, setSaving] = useState(false);
  const [tariff, setTariff] = useState('');
  const [topup, setTopup] = useState('');
  const [drgCode, setDrgCode] = useState('');
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [errors, setErrors] = useState({});

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;
  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  useEffect(() => {
    if (open) {
      const preChecked = {};
      insuranceProviders.forEach(i => {
        if (existingStatuses.includes(i.name)) preChecked[i.id] = true;
      });
      setTariff(''); setTopup(''); setDrgCode('');
      setSelectedInsurances(preChecked); setInsuranceSearch(''); setErrors({});
    }
  }, [open, insuranceProviders, existingStatuses]);

  const handleInsuranceToggle = (id) => { setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] })); clearError('insurance'); };
  const handleSelectAll = () => { const all = {}; insuranceProviders.forEach(i => { if (!existingStatuses.includes(i.name)) all[i.id] = true; }); setSelectedInsurances(all); clearError('insurance'); };
  const handleClose = () => { setTariff(''); setTopup(''); setDrgCode(''); setSelectedInsurances({}); setInsuranceSearch(''); setErrors({}); onClose(); };

  const validate = () => {
    const e = {};
    if (tariff === '' || tariff === null) e.tariff = 'Required'; else if (isNaN(parseFloat(tariff)) || parseFloat(tariff) < 0) e.tariff = 'Invalid';
    if (topup === '' || topup === null) e.topup = 'Required'; else if (isNaN(parseFloat(topup)) || parseFloat(topup) < 0) e.topup = 'Invalid';
    if (selectedCount === 0) e.insurance = 'Select at least one insurance';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return { scan: selectedScan.scan, tariff: parseFloat(tariff), topup: parseFloat(topup), GDRG: drgCode.trim(), status: provider?.name || '' };
      });
      const response = await axios.post(saveNewScan, { rows });
      if (response.data.success) {
        await Swal.fire({ title: 'Saved!', html: `<b>${selectedScan.scan}</b> priced for <b>${selectedCount}</b> insurance${selectedCount !== 1 ? 's' : ''}.`, icon: 'success', timer: 2500, showConfirmButton: false, timerProgressBar: true });
        if (onSaved) onSaved(); handleClose();
      } else throw new Error(response.data.message || 'Save failed');
    } catch (err) {
      await Swal.fire({ title: 'Save Failed', text: err.response?.data?.message || err.message, icon: 'error' });
    } finally { setSaving(false); }
  };

  const filteredProviders = insuranceProviders.filter(i => i.name?.toLowerCase().includes(insuranceSearch.toLowerCase()));
  const availableProviders = filteredProviders.filter(i => !existingStatuses.includes(i.name));
  const alreadyAssigned = filteredProviders.filter(i => existingStatuses.includes(i.name));

  if (!selectedScan) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade} PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{ background: 'linear-gradient(135deg,#11998e,#38ef7d)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ScannerIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>New Insurance Pricing</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>{selectedScan.scan}</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>

        <Grid container sx={{ minHeight: 440 }} columns={{ xs: 4, sm: 8, md: 12 }}>

          <Grid item xs={12} md={5} sx={{ p: 3, borderRight: { md: '1px solid #e8eaf6' }, background: '#fafbff' }} size={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3949ab', mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>Set Pricing</Typography>
            <TextField fullWidth size="small" label="Tariff GH¢ *" type="number" value={tariff} onChange={e => { setTariff(e.target.value); clearError('tariff'); }} error={!!errors.tariff} helperText={errors.tariff} inputProps={{ min: 0, step: '0.01' }} sx={{ mb: 2 }} />
            <TextField fullWidth size="small" label="Top-up GH¢ *" type="number" value={topup} onChange={e => { setTopup(e.target.value); clearError('topup'); }} error={!!errors.topup} helperText={errors.topup} inputProps={{ min: 0, step: '0.01' }} sx={{ mb: 2 }} />
            <Divider sx={{ my: 1.5 }} />
            <TextField fullWidth size="small" label="DRG Code (optional)" value={drgCode} onChange={e => setDrgCode(e.target.value)} sx={{ mb: 2 }} placeholder="e.g., SCAN001" />
            <Alert severity="info" sx={{ fontSize: '0.72rem', mt: 1 }}><strong>Tariff:</strong> Base insurance tariff<br /><strong>Top-up:</strong> Additional patient fee (this is what the patient bill uses)</Alert>
          </Grid>


          <Grid item xs={12} md={7} sx={{ p: 3, display: 'flex', flexDirection: 'column', background: '#fff' }} size={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: errors.insurance ? '#e53935' : '#3949ab', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>Select Insurance Providers *</Typography>
                <Typography variant="caption" color={errors.insurance ? 'error' : 'text.secondary'}>{selectedCount} selected | {availableProviders.length} available</Typography>
              </Box>
              {selectedCount > 0 && <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />} label={`${selectedCount}`} sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }} />}
            </Box>
            {errors.insurance && <Typography variant="caption" sx={{ color: '#e53935', fontSize: '0.7rem', mb: 0.5 }}>{errors.insurance}</Typography>}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll} sx={{ borderColor: '#11998e', color: '#11998e', fontSize: '0.72rem', py: 0.4 }}>Select All Available</Button>
              <Button size="small" variant="outlined" onClick={() => setSelectedInsurances({})} sx={{ borderColor: '#11998e', color: '#11998e', fontSize: '0.72rem', py: 0.4 }}>Deselect All</Button>
            </Box>
            <TextField fullWidth size="small" placeholder="Search insurance..." value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5 }}
              InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} /></InputAdornment> }} />
            <Box sx={{ flex: 1, overflowY: 'auto', border: `1px solid ${errors.insurance ? '#e53935' : '#e8eaf6'}`, borderRadius: 2, p: 1, minHeight: 200, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' } }}>
              {loadingInsurance ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box> : (
                <>
                  {availableProviders.length > 0 && <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#2e7d32', display: 'block', mb: 0.5, px: 0.5 }}>Available ({availableProviders.length})</Typography>
                    {availableProviders.map(ins => (
                      <Box key={ins.id} onClick={() => handleInsuranceToggle(ins.id)} sx={{ display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1, cursor: 'pointer', mb: 0.5, background: selectedInsurances[ins.id] ? 'rgba(17,153,142,0.08)' : 'transparent', border: selectedInsurances[ins.id] ? '1px solid rgba(17,153,142,0.25)' : '1px solid transparent', '&:hover': { background: 'rgba(17,153,142,0.06)' } }}>
                        <Checkbox size="small" checked={!!selectedInsurances[ins.id]} onClick={e => e.stopPropagation()} onChange={() => handleInsuranceToggle(ins.id)} sx={{ p: 0.5, color: '#11998e', '&.Mui-checked': { color: '#11998e' } }} />
                        <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem' }}>{ins.name}</Typography>
                      </Box>
                    ))}
                  </Box>}

                  {alreadyAssigned.length > 0 && <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#e53935', display: 'block', mb: 0.5, px: 0.5 }}>
                      Already Priced ({alreadyAssigned.length}) — uncheck to skip
                    </Typography>
                    {alreadyAssigned.map(ins => (
                      <Box key={ins.id} onClick={() => handleInsuranceToggle(ins.id)}
                        sx={{
                          display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                          cursor: 'pointer', mb: 0.5,
                          background: selectedInsurances[ins.id] ? 'rgba(229,57,53,0.06)' : 'transparent',
                          border: selectedInsurances[ins.id] ? '1px solid rgba(229,57,53,0.25)' : '1px solid transparent',
                          '&:hover': { background: 'rgba(229,57,53,0.04)' },
                        }}>
                        <Checkbox size="small"
                          checked={!!selectedInsurances[ins.id]}
                          onClick={e => e.stopPropagation()}
                          onChange={() => handleInsuranceToggle(ins.id)}
                          sx={{ p: 0.5, color: '#e53935', '&.Mui-checked': { color: '#e53935' } }} />
                        <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: '#424242' }}>{ins.name}</Typography>
                        <Chip label="Exists" size="small"
                          sx={{ ml: 'auto', fontSize: '0.65rem', height: 18, background: '#fce4ec', color: '#c62828' }} />
                      </Box>
                    ))}
                  </Box>}

                  {availableProviders.length === 0 && alreadyAssigned.length === 0 && <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}><Typography variant="body2" color="text.secondary">No providers found</Typography></Box>}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0', background: '#fafafa', gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" disabled={saving} sx={{ color: '#757575', borderColor: '#e0e0e0' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ background: 'linear-gradient(90deg,#11998e,#38ef7d)', color: 'white', fontWeight: 700, px: 3, '&:hover': { background: 'linear-gradient(90deg,#0e8a7f,#2fd469)' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
          {saving ? 'Saving...' : 'Save Pricing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Main Component ─────────────────────────────────────── */
const Scan = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [scanGroups, setScanGroups] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [loadingScans, setLoadingScans] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedScan, setSelectedScan] = useState(null);
  const [insuranceRows, setInsuranceRows] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [newPricingDialogOpen, setNewPricingDialogOpen] = useState(false);
  const [bulkApplyDialogOpen, setBulkApplyDialogOpen] = useState(false);
  const [sourceRowForBulkApply, setSourceRowForBulkApply] = useState(null);

  // ── Move scans state ──────────────────────────────────
  const [selectMode, setSelectMode]                   = useState(false);
  const [selectedScanNames, setSelectedScanNames]     = useState(new Set());
  const [moveDialogOpen, setMoveDialogOpen]           = useState(false);
  const [moveTargetInsurance, setMoveTargetInsurance] = useState('');
  const [moving, setMoving]                           = useState(false);

  const [insuranceFilter, setInsuranceFilter] = useState('');

  const loadInsuranceProviders = async () => {
    setLoadingInsurance(true); setError(null);
    try { const res = await axios.get(loadInsurance); if (res.data.success) setInsuranceProviders(res.data.results); else setError('Failed to load insurance'); }
    catch (err) { setError(err.response?.data?.message || 'Failed to load insurance'); }
    finally { setLoadingInsurance(false); }
  };

  const loadScanGroups = async () => {
    setLoadingScans(true); setError(null);
    try { const res = await axios.get(loadScanCategoriesbd); if (res.data.success) setScanGroups(res.data.results); else setError('Failed to load scans'); }
    catch (err) { setError(err.response?.data?.message || 'Failed to load scans'); }
    finally { setLoadingScans(false); }
  };

  const loadInsuranceRecords = async (scan) => {
    setLoadingRecords(true); setInsuranceRows([]); setSelectedRowIds([]);
    try { const res = await axios.post(getScanInsurances, { scanName: scan.scan }); if (res.data.success) setInsuranceRows(res.data.results); }
    catch (err) { await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load records', icon: 'error' }); }
    finally { setLoadingRecords(false); }
  };

  useEffect(() => { loadInsuranceProviders(); loadScanGroups(); }, []);

  const handleScanSelect = async (scan) => {
    if (selectedScan?.scan === scan.scan) { setSelectedScan(null); setInsuranceRows([]); setSelectedRowIds([]); return; }
    setSelectedScan(scan);
    await loadInsuranceRecords(scan);
  };

  const handleRowChange = (idx, field, value) => setInsuranceRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const handleUpdateRow = async (row) => {
    if (row.tariff === '' || row.topup === '') {
      await Swal.fire({ title: 'Validation', text: 'Fill in tariff and top-up', icon: 'warning' }); return;
    }
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record id missing', icon: 'error' }); return; }
    const rowId = String(row.id);
    setUpdatingRowId(rowId);
    try {
      const res = await axios.post(updateInsuredScan, {
        id: row.id, scan: row.scan, status: row.status,
        tariff: parseFloat(row.tariff), topup: parseFloat(row.topup),
        GDRG: row.GDRG || ''
      });
      if (res.data.success) await Swal.fire({ title: 'Updated!', text: `${row.scan} — ${row.status}`, icon: 'success', timer: 2000, showConfirmButton: false });
      else throw new Error(res.data.message || 'Update failed');
    } catch (err) { await Swal.fire({ title: 'Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setUpdatingRowId(null); }
  };

  const handleDeleteRow = async (row) => {
    if (!row.id) return;
    const result = await Swal.fire({ title: 'Delete?', html: `Delete pricing for <strong>${row.status}</strong>?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53935', cancelButtonColor: '#9e9e9e', confirmButtonText: 'Delete' });
    if (!result.isConfirmed) return;
    const rowId = String(row.id);
    setDeletingRowId(rowId);
    try {
      const res = await axios.post(deleteInsuredScan, { id: row.id });
      if (res.data.success) {
        await Swal.fire({ title: 'Deleted!', icon: 'success', timer: 1500, showConfirmButton: false });
        setInsuranceRows(prev => prev.filter(r => r.id !== row.id));
        setSelectedRowIds(prev => prev.filter(id => id !== rowId));
      } else throw new Error(res.data.message || 'Delete failed');
    } catch (err) { await Swal.fire({ title: 'Failed', text: err.response?.data?.message || err.message, icon: 'error' }); }
    finally { setDeletingRowId(null); }
  };

  const getRowId = (row) => String(row.id);
  const handleRowSelectionToggle = (id) => setSelectedRowIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleSelectAllRows = () => { if (selectedRowIds.length === insuranceRows.length) setSelectedRowIds([]); else setSelectedRowIds(insuranceRows.map(r => getRowId(r))); };

  const handleBulkApply = () => {
    if (selectedRowIds.length === 0) {
      Swal.fire({ title: 'No Selection', text: 'Select at least one row.', icon: 'info', confirmButtonColor: '#11998e' });
      return;
    }
    const sourceRow = insuranceRows.find(r => getRowId(r) === selectedRowIds[0]);
    if (!sourceRow) return;
    setSourceRowForBulkApply(sourceRow);
    setBulkApplyDialogOpen(true);
  };

  const filteredScans = scanGroups
    .filter((s, idx, arr) => arr.findIndex(x => x.scan === s.scan) === idx)
    .filter(s => s.scan?.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(s => !insuranceFilter || s.insurances?.some(i => i.name === insuranceFilter));

  const existingStatuses = insuranceRows.map(r => r.status);


  const toggleScanSelection = (scanName) => {
    setSelectedScanNames(prev => {
      const next = new Set(prev);
      next.has(scanName) ? next.delete(scanName) : next.add(scanName);
      return next;
    });
  };

  const handleSelectAllVisibleScans = (visibleScans) => {
    setSelectedScanNames(new Set(visibleScans.map(s => s.scan)));
  };

  const handleDeselectAllScans = () => setSelectedScanNames(new Set());

  const handleMoveScans = async () => {
    if (selectedScanNames.size === 0 || !moveTargetInsurance) return;
    setMoving(true);
    try {
      const res = await axios.post(moveScanToInsurancedb, {
        scanNames:       Array.from(selectedScanNames),
        targetInsurance: moveTargetInsurance,
      });
      if (res.data.success) {
        await Swal.fire({
          title: 'Done!',
          text: res.data.message,
          icon: 'success',
          timer: 3000,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        setMoveDialogOpen(false);
        setSelectedScanNames(new Set());
        setMoveTargetInsurance('');
        setSelectMode(false);
        loadScanGroups();
      } else {
        throw new Error(res.data.message);
      }
    } catch (err) {
      await Swal.fire({ title: 'Move Failed', text: err.response?.data?.message || err.message, icon: 'error' });
    } finally {
      setMoving(false);
    }
  };

  return (
    <>
      <Paper elevation={8} sx={{ margin: '0 auto', borderRadius: 3, overflow: 'hidden', background: '#ffffff' }}>
        <Box sx={{ background: 'linear-gradient(135deg,#11998e,#38ef7d)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '0.5px', textShadow: '1px 2px 4px rgba(0,0,0,0.15)' }}>Scan Tariff Management</Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="contained" onClick={() => { loadScanGroups(); loadInsuranceProviders(); }} disabled={loadingScans}
              startIcon={loadingScans ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              size="small" sx={{ background: 'rgba(255,255,255,0.2)', '&:hover': { background: 'rgba(255,255,255,0.3)' } }}>
              {loadingScans ? 'Loading...' : 'Reload'}
            </Button>
            <Tooltip title={selectedScan ? 'Add insurance pricing' : 'Select a scan first'}>
              <span>
                <Button variant="contained" startIcon={<AddIcon />} size="small"
                  onClick={() => { if (!selectedScan) { Swal.fire({ title: 'Select a Scan', text: 'Click a scan on the left first.', icon: 'info', confirmButtonColor: '#11998e' }); return; } setNewPricingDialogOpen(true); }}
                  sx={{ background: 'rgba(255,255,255,0.95)', color: '#11998e', fontWeight: 700, '&:hover': { background: '#fff' } }}>
                  New Pricing
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>

            {/* LEFT: Scan list */}
            <Grid item xs={12} md={4} size={4}>
              <Paper elevation={0} sx={{ p: 2.5, border: '2px solid #e0e0e0', borderRadius: 2, background: '#fff', height: 600, display: 'flex', flexDirection: 'column' }}>

                {/* Insurance filter Select */}
                <Box sx={{ mb: insuranceFilter ? 1 : 1.5 }}>
                  <select
                    value={insuranceFilter}
                    onChange={e => setInsuranceFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      fontSize: '0.82rem',
                      borderRadius: '6px',
                      border: `1px solid ${insuranceFilter ? '#11998e' : '#e0e0e0'}`,
                      outline: 'none',
                      color: insuranceFilter ? '#0e8a7f' : '#9e9e9e',
                      fontWeight: insuranceFilter ? 600 : 400,
                      background: insuranceFilter ? 'rgba(17,153,142,0.04)' : '#fafafa',
                      cursor: 'pointer',
                      appearance: 'auto',
                    }}
                  >
                    <option value="">All Insurances</option>
                    {insuranceProviders.map(ins => (
                      <option key={ins.id} value={ins.name}>{ins.name}</option>
                    ))}
                  </select>
                </Box>

                {/* Active filter chip */}
                {insuranceFilter && (
                  <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip size="small"
                      icon={<InsuranceIcon sx={{ fontSize: '13px !important' }} />}
                      label={insuranceFilter}
                      onDelete={() => setInsuranceFilter('')}
                      sx={{ fontSize: '0.72rem', bgcolor: 'rgba(17,153,142,0.1)', color: '#0e8a7f', fontWeight: 600, height: 22 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                      — filtering scans
                    </Typography>
                  </Box>
                )}

                {/* Search */}
                <TextField fullWidth size="small" placeholder="Search scans..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} sx={{ mb: 2 }}
                  InputProps={{ endAdornment: <InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#11998e' }} /></InputAdornment> }} />

                {/* Header row with Move toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e' }}>Available Scans</Typography>
                    <Chip size="small" label={`${filteredScans.length} total`} sx={{ fontSize: '0.68rem', background: '#e8eaf6', color: '#3949ab' }} />
                  </Box>
                  <Tooltip title={selectMode ? 'Exit selection mode' : 'Select scans to copy to another insurance'}>
                    <Button size="small" variant={selectMode ? 'contained' : 'outlined'}
                      startIcon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                      onClick={() => { setSelectMode(s => !s); setSelectedScanNames(new Set()); }}
                      sx={{
                        fontSize: '0.68rem', py: 0.3, px: 1,
                        borderColor: '#11998e', color: selectMode ? 'white' : '#11998e',
                        bgcolor: selectMode ? '#11998e' : 'transparent',
                        '&:hover': { bgcolor: selectMode ? '#0e8a7f' : 'rgba(17,153,142,0.06)' }
                      }}>
                      {selectMode ? 'Cancel' : 'Move'}
                    </Button>
                  </Tooltip>
                </Box>

                {/* Selection action bar */}
                {selectMode && (
                  <Box sx={{ mb: 1, p: 1, borderRadius: 1.5, bgcolor: 'rgba(17,153,142,0.06)', border: '1px solid rgba(17,153,142,0.2)', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#11998e', fontSize: '0.72rem' }}>
                      {selectedScanNames.size} selected
                    </Typography>
                    <Button size="small" variant="text"
                      onClick={() => handleSelectAllVisibleScans(filteredScans)}
                      sx={{ fontSize: '0.68rem', py: 0.2, color: '#11998e', minWidth: 0 }}>
                      All
                    </Button>
                    <Button size="small" variant="text" onClick={handleDeselectAllScans}
                      sx={{ fontSize: '0.68rem', py: 0.2, color: '#9e9e9e', minWidth: 0 }}>
                      None
                    </Button>
                    {selectedScanNames.size > 0 && (
                      <Button size="small" variant="contained"
                        startIcon={<SwapHorizIcon sx={{ fontSize: 13 }} />}
                        onClick={() => setMoveDialogOpen(true)}
                        sx={{ ml: 'auto', fontSize: '0.68rem', py: 0.3, px: 1.2, bgcolor: '#11998e', '&:hover': { bgcolor: '#0e8a7f' } }}>
                        Move {selectedScanNames.size}
                      </Button>
                    )}
                  </Box>
                )}

                {/* Selected scan indicator (only in normal mode) */}
                {selectedScan && !selectMode && (
                  <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, background: 'rgba(17,153,142,0.08)', border: '1px solid rgba(17,153,142,0.2)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#11998e' }} />
                    <Typography variant="caption" sx={{ color: '#11998e', fontWeight: 600, flex: 1 }} noWrap>Selected: {selectedScan.scan}</Typography>
                    <IconButton size="small" onClick={() => { setSelectedScan(null); setInsuranceRows([]); setSelectedRowIds([]); }} sx={{ p: 0.2 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                )}

                {/* Scan list */}
                <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' } }}>
                  {loadingScans
                    ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
                    : filteredScans.length === 0
                      ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 1 }}>
                          <ScannerIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
                          <Typography variant="body2" color="text.secondary">No scans found</Typography>
                        </Box>
                      : filteredScans.map((scan, idx) => {
                          const isSelected = selectedScan?.scan === scan.scan && !selectMode;
                          const isChecked  = selectedScanNames.has(scan.scan);
                          return (
                            <Box key={`${scan.scan}-${idx}`}
                              onClick={() => selectMode ? toggleScanSelection(scan.scan) : handleScanSelect(scan)}
                              sx={{
                                display: 'flex', alignItems: 'center', mb: 0.75, p: 1.25, borderRadius: 1.5,
                                cursor: 'pointer', transition: 'all 0.18s ease',
                                border: isSelected  ? '1.5px solid #11998e'
                                       : isChecked  ? '1.5px solid #6ee7b7'
                                       : '1.5px solid transparent',
                                background: isSelected  ? 'rgba(17,153,142,0.08)'
                                          : isChecked   ? 'rgba(17,153,142,0.05)'
                                          : 'transparent',
                                '&:hover': { background: 'rgba(17,153,142,0.05)', transform: 'translateX(3px)', borderColor: 'rgba(17,153,142,0.3)' }
                              }}>
                              {selectMode ? (
                                <Checkbox size="small" checked={isChecked}
                                  onChange={() => toggleScanSelection(scan.scan)}
                                  onClick={e => e.stopPropagation()}
                                  sx={{ p: 0.3, color: '#11998e', '&.Mui-checked': { color: '#11998e' }, mr: 0.5 }} />
                              ) : (
                                <MedicalIcon sx={{ fontSize: 16, mr: 1, color: isSelected ? '#11998e' : '#bdbdbd' }} />
                              )}
                              <Typography variant="body2" noWrap sx={{ fontSize: '0.85rem', flex: 1,
                                color: isSelected || isChecked ? '#11998e' : '#424242',
                                fontWeight: isSelected || isChecked ? 700 : 400 }}>
                                {scan.scan}
                              </Typography>
                              {isChecked && selectMode && (
                                <CheckCircleIcon sx={{ fontSize: 14, color: '#11998e', ml: 'auto' }} />
                              )}
                            </Box>
                          );
                        })
                  }
                </Box>
              </Paper>
            </Grid>

            {/* RIGHT: Insurance records */}
            <Grid item xs={12} md={8} size={8}>
              <Paper elevation={0} sx={{ border: '2px solid #e0e0e0', borderRadius: 2, background: '#fff', height: 600, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e8eaf6', background: '#f8f9ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e' }}>Insurance Coverage Records</Typography>
                    <Typography variant="caption" color="text.secondary">{selectedScan ? `Records for: ${selectedScan.scan}` : 'Select a scan to load records'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {selectedRowIds.length > 0 && <Chip size="small" label={`${selectedRowIds.length} selected`} sx={{ background: '#e3f2fd', color: '#1976d2', fontWeight: 600, fontSize: '0.7rem' }} />}
                    {selectedScan && !loadingRecords && <Chip icon={<InsuranceIcon sx={{ fontSize: '14px !important' }} />} label={`${insuranceRows.length} record${insuranceRows.length !== 1 ? 's' : ''}`} size="small" sx={{ background: insuranceRows.length > 0 ? '#e8f5e9' : '#fce4ec', color: insuranceRows.length > 0 ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: '0.7rem' }} />}
                  </Box>
                </Box>

                {selectedScan && insuranceRows.length > 0 && (
                  <Box sx={{ px: 2.5, py: 1, borderBottom: '1px solid #e8eaf6', background: '#fafafa', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                    <Button size="small" variant="outlined" onClick={handleSelectAllRows} sx={{ fontSize: '0.72rem', py: 0.4, borderColor: '#11998e', color: '#11998e' }}>
                      {selectedRowIds.length === insuranceRows.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button size="small" variant="contained" startIcon={<CopyIcon />} onClick={handleBulkApply}
                      disabled={selectedRowIds.length === 0}
                      sx={{ fontSize: '0.72rem', py: 0.4, background: '#11998e', '&:hover': { background: '#0e8a7f' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
                      Apply to Insurances
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                      {selectedRowIds.length === 0 && 'Select rows to apply pricing to multiple insurances'}
                      {selectedRowIds.length >= 1 && `${selectedRowIds.length} row${selectedRowIds.length !== 1 ? 's' : ''} selected — click "Apply to Insurances"`}
                    </Typography>
                  </Box>
                )}

                {!selectedScan ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, opacity: 0.4 }}>
                    <InsuranceIcon sx={{ fontSize: 56, color: '#90caf9' }} />
                    <Typography variant="body2" color="text.secondary" textAlign="center">Click any scan on the left<br />to view its insurance pricing</Typography>
                  </Box>
                ) : loadingRecords ? (
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box textAlign="center"><CircularProgress size={36} sx={{ color: '#11998e' }} /><Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#9e9e9e' }}>Loading...</Typography></Box>
                  </Box>
                ) : insuranceRows.length === 0 ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                    <InsuranceIcon sx={{ fontSize: 44, color: '#ef9a9a' }} />
                    <Typography variant="body2" color="text.secondary">No insurance pricing found.</Typography>
                    <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setNewPricingDialogOpen(true)}
                      sx={{ mt: 1, background: '#11998e', '&:hover': { background: '#0e8a7f' }, textTransform: 'none', fontWeight: 600 }}>
                      Add Insurance Pricing
                    </Button>
                  </Box>
                ) : (
                  <TableContainer sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' } }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 850 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ background: '#11998e', color: '#fff', width: 48, textAlign: 'center', py: 1.2, px: 0.5 }}>
                            <Checkbox size="small" checked={selectedRowIds.length === insuranceRows.length && insuranceRows.length > 0}
                              indeterminate={selectedRowIds.length > 0 && selectedRowIds.length < insuranceRows.length}
                              onChange={handleSelectAllRows} sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' }, p: 0.5 }} />
                          </TableCell>
                          {['#', 'Insurance', 'Tariff (GH¢)', 'Top-up', 'DRG Code', 'Actions'].map(label => (
                            <TableCell key={label} sx={{ background: '#11998e', color: '#fff', fontWeight: 600, fontSize: '0.72rem', letterSpacing: '0.3px', borderBottom: 'none', py: 1.2, whiteSpace: 'nowrap', ...(label === '#' && { width: 36, textAlign: 'center' }), ...(label === 'Actions' && { width: 180, textAlign: 'center' }) }}>{label}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {insuranceRows.map((row, idx) => {
                          const rowId = getRowId(row);
                          return (
                            <EditableRow key={`${rowId}-${idx}`} row={row} idx={idx}
                              selected={selectedRowIds.includes(rowId)} onSelectToggle={handleRowSelectionToggle}
                              onChange={(field, value) => handleRowChange(idx, field, value)}
                              onUpdate={handleUpdateRow} onDelete={handleDeleteRow}
                              updating={updatingRowId === rowId} deleting={deletingRowId === rowId} />
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>


      {/* ══════════ Move Scans Dialog ══════════ */}
      <Dialog open={moveDialogOpen} onClose={() => !moving && setMoveDialogOpen(false)}
        maxWidth="xs" fullWidth TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ background: 'linear-gradient(135deg,#11998e,#38ef7d)', color: 'white', px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SwapHorizIcon sx={{ fontSize: 26 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>Copy Scans</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {selectedScanNames.size} scan{selectedScanNames.size !== 1 ? 's' : ''} selected
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setMoveDialogOpen(false)} disabled={moving} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 1 }}>

          {/* Selected scans chips */}
          <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 2, bgcolor: '#f4faf7', border: '1px solid #c6e9df', maxHeight: 130, overflowY: 'auto' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#0e8a7f', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem', display: 'block', mb: 1 }}>
              Scans to copy:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {Array.from(selectedScanNames).map(name => (
                <Chip key={name} label={name} size="small"
                  sx={{ fontSize: '0.72rem', height: 20, bgcolor: 'rgba(17,153,142,0.1)', color: '#0e8a7f' }} />
              ))}
            </Box>
          </Box>

          {/* Target insurance picker */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', display: 'block', mb: 0.75 }}>
            Copy to Insurance *
          </Typography>
          <Box sx={{ border: `1px solid ${moveTargetInsurance ? '#11998e' : '#e0e0e0'}`, borderRadius: 1.5, overflow: 'hidden', maxHeight: 220, overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' } }}>
            {insuranceProviders.map(ins => (
              <Box key={ins.id}
                onClick={() => setMoveTargetInsurance(ins.name)}
                sx={{
                  display: 'flex', alignItems: 'center', px: 2, py: 1,
                  cursor: 'pointer', transition: 'all 0.15s',
                  background: moveTargetInsurance === ins.name ? 'rgba(17,153,142,0.1)' : 'transparent',
                  borderLeft: moveTargetInsurance === ins.name ? '3px solid #11998e' : '3px solid transparent',
                  '&:hover': { background: 'rgba(17,153,142,0.06)' }
                }}>
                <InsuranceIcon sx={{ fontSize: 15, color: moveTargetInsurance === ins.name ? '#11998e' : '#bdbdbd', mr: 1 }} />
                <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: moveTargetInsurance === ins.name ? 700 : 400,
                  color: moveTargetInsurance === ins.name ? '#0e8a7f' : '#424242' }}>
                  {ins.name}
                </Typography>
                {moveTargetInsurance === ins.name && (
                  <CheckCircleIcon sx={{ fontSize: 14, color: '#11998e', ml: 'auto' }} />
                )}
              </Box>
            ))}
          </Box>

          <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem', py: 0.5 }}>
            Scans already assigned to the target insurance will be skipped automatically.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => { setMoveDialogOpen(false); setMoveTargetInsurance(''); }}
            disabled={moving} variant="outlined"
            sx={{ color: '#757575', borderColor: '#e0e0e0', flex: 1 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleMoveScans}
            disabled={moving || !moveTargetInsurance}
            startIcon={moving ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}
            sx={{ flex: 1, bgcolor: '#11998e', fontWeight: 700, '&:hover': { bgcolor: '#0e8a7f' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
            {moving ? 'Copying…' : `Copy ${selectedScanNames.size}`}
          </Button>
        </DialogActions>
      </Dialog>

      <NewScanPricingDialog open={newPricingDialogOpen} onClose={() => setNewPricingDialogOpen(false)}
        selectedScan={selectedScan} insuranceProviders={insuranceProviders} loadingInsurance={loadingInsurance}
        onSaved={() => { if (selectedScan) loadInsuranceRecords(selectedScan); }} existingStatuses={existingStatuses} />

      <BulkApplyDialog
        open={bulkApplyDialogOpen}
        onClose={() => { setBulkApplyDialogOpen(false); setSourceRowForBulkApply(null); }}
        sourceRow={sourceRowForBulkApply}
        selectedRows={insuranceRows.filter(r => selectedRowIds.includes(getRowId(r)))}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={() => { if (selectedScan) loadInsuranceRecords(selectedScan); }}
        existingStatuses={existingStatuses}
      />
    </>
  );
};

export default Scan;
